import { useEffect, useState, useRef, useCallback } from "react";
import { api, fmtBytes, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ThroughputChart from "@/components/ThroughputChart";
import SchedulePanel from "@/components/SchedulePanel";
import CrawlPresets from "@/components/CrawlPresets";
import PromptToCrawl from "@/components/PromptToCrawl";
import UploadFiles from "@/components/UploadFiles";
import EmpireSource from "@/components/EmpireSource";
import CrawlId from "@/components/CrawlId";
import {
  Play, Square, Plus, Activity, Layers, Gauge, Clock, HardDrive, FileText, CircleDot, Zap,
  Pause, RotateCcw, ShieldCheck, Ban, TrendingUp, Map, Search, Loader2, History, Award,
} from "lucide-react";

const EXTRACTION_MODES = [
  { v: "clean_text", label: "Clean Text (recommended)" },
  { v: "markdown", label: "Markdown" },
  { v: "raw_html", label: "Raw HTML" },
  { v: "text_links_meta", label: "Text + Links + Metadata" },
];

const LOG_COLORS = {
  INFO: "text-slate-400",
  EXTRACT: "text-cyan-400",
  WARN: "text-amber-400",
  ERROR: "text-rose-400",
  ROBOTS: "text-purple-400",
};

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-md border border-border bg-[#0E1420] px-3 py-3">
      <div className="flex items-center gap-1.5 text-slate-500">
        <Icon className={cn("h-3.5 w-3.5", accent)} />
        <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-mono font-black text-xl mt-1 text-white tabular-nums">{value}</p>
    </div>
  );
}

export default function ControlRoom({ crawls, activeCrawl, setActiveId, refresh }) {
  const [form, setForm] = useState({
    name: "", keywords: "", seed_urls: "", extraction_mode: "clean_text",
    relevance_keywords: "", relevance_match: "any", time_limit_seconds: 300,
    max_pages: 200, same_domain_only: false,
    respect_robots: true, per_domain_delay_ms: 1000, concurrency: 4,
    sitemap_urls: "", sitemap_filter: "", sitemap_limit: 5000,
    llm_quality_check: false, llm_quality_threshold: 0.3,
  });
  const [sitemapInfo, setSitemapInfo] = useState(null);
  const [smBusy, setSmBusy] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("live");
  const [runDetail, setRunDetail] = useState(null);
  const [detail, setDetail] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef(null);
  const clockRef = useRef({ base: 0, at: Date.now() });

  useEffect(() => {
    api.getCrawlerSettings()
      .then((s) => setForm((f) => ({
        ...f,
        respect_robots: s.respect_robots,
        per_domain_delay_ms: s.per_domain_delay_ms,
        concurrency: s.concurrency,
      })))
      .catch(() => {});
  }, []);

  const poll = useCallback(async () => {
    if (!activeCrawl) { setDetail(null); return; }
    try {
      const data = await api.getCrawl(activeCrawl.id);
      setDetail(data);
      clockRef.current = { base: Number(data.crawl.elapsed_seconds || 0), at: Date.now() };
      if (data.crawl.status !== "running") setElapsed(Number(data.crawl.elapsed_seconds || 0));
    } catch (e) { /* noop */ }
  }, [activeCrawl]);

  useEffect(() => { poll(); }, [poll]);

  useEffect(() => {
    const running = detail?.crawl?.status === "running";
    if (!running) return;
    const t = setInterval(() => { poll(); refresh(); }, 1500);
    return () => clearInterval(t);
  }, [detail?.crawl?.status, poll, refresh]);

  useEffect(() => {
    if (detail?.crawl?.status !== "running") return;
    const t = setInterval(() => {
      const { base, at } = clockRef.current;
      setElapsed(base + (Date.now() - at) / 1000);
    }, 400);
    return () => clearInterval(t);
  }, [detail?.crawl?.status]);

  useEffect(() => {
    if (autoScroll && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [detail?.logs, autoScroll]);

  const crawl = detail?.crawl || activeCrawl;
  const isRunning = crawl?.status === "running";
  const canResume = !isRunning && crawl?.resumable;

  const loadRuns = useCallback(async () => {
    if (!activeCrawl) { setRuns([]); return; }
    try {
      const d = await api.listRuns(activeCrawl.id);
      setRuns(d.runs);
    } catch (e) { /* noop */ }
  }, [activeCrawl]);

  useEffect(() => { setRunId("live"); setRunDetail(null); loadRuns(); }, [loadRuns]);
  useEffect(() => { if (!isRunning) loadRuns(); }, [isRunning, loadRuns]);

  useEffect(() => {
    if (runId === "live") { setRunDetail(null); return; }
    api.getRun(runId).then(setRunDetail).catch(() => setRunDetail(null));
  }, [runId]);

  const viewingLive = runId === "live";
  const logs = viewingLive ? detail?.logs : runDetail?.logs;
  const samples = viewingLive ? detail?.live?.samples : runDetail?.samples;
  const statusCounts = viewingLive ? detail?.live?.status_counts : runDetail?.status_counts;

  const previewSitemap = async () => {
    const src = form.sitemap_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)[0];
    if (!src) { toast.error("Enter a domain or sitemap.xml URL first"); return; }
    setSmBusy(true);
    setSitemapInfo(null);
    try {
      setSitemapInfo(await api.discoverSitemap({
        url: src, url_filter: form.sitemap_filter, limit: Number(form.sitemap_limit) || 5000,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No sitemap found");
    } finally { setSmBusy(false); }
  };

  const clearHistory = async () => {
    await api.clearRuns(activeCrawl.id);
    toast.success("Run history cleared");
    setRunId("live");
    loadRuns();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildConfig = () => ({
    name: form.name.trim() || `Crawl ${new Date().toLocaleTimeString()}`,
    keywords: form.keywords.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    seed_urls: form.seed_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    extraction_mode: form.extraction_mode,
    relevance_keywords: form.relevance_keywords.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    relevance_match: form.relevance_keywords.trim() ? form.relevance_match : "off",
    sitemap_urls: form.sitemap_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    sitemap_filter: form.sitemap_filter.trim(),
    sitemap_limit: Number(form.sitemap_limit) || 5000,
    time_limit_seconds: Number(form.time_limit_seconds),
    max_pages: Number(form.max_pages),
    same_domain_only: form.same_domain_only,
    respect_robots: form.respect_robots,
    per_domain_delay_ms: Number(form.per_domain_delay_ms),
    concurrency: Number(form.concurrency),
    llm_quality_check: form.llm_quality_check,
    llm_quality_threshold: Number(form.llm_quality_threshold),
  });

  const applyConfig = (cfg) => {
    setForm((f) => ({
      ...f,
      name: cfg.name || "",
      keywords: (cfg.keywords || []).join(", "),
      seed_urls: (cfg.seed_urls || []).join("\n"),
      extraction_mode: cfg.extraction_mode || "clean_text",
      relevance_keywords: (cfg.relevance_keywords || []).join(", "),
      relevance_match: cfg.relevance_match === "off" ? "any" : cfg.relevance_match || "any",
      sitemap_urls: (cfg.sitemap_urls || []).join("\n"),
      sitemap_filter: cfg.sitemap_filter || "",
      sitemap_limit: cfg.sitemap_limit ?? 5000,
      time_limit_seconds: cfg.time_limit_seconds ?? 300,
      max_pages: cfg.max_pages ?? 200,
      same_domain_only: !!cfg.same_domain_only,
      llm_quality_check: !!cfg.llm_quality_check,
      llm_quality_threshold: cfg.llm_quality_threshold ?? 0.3,
      respect_robots: cfg.respect_robots ?? f.respect_robots,
      per_domain_delay_ms: cfg.per_domain_delay_ms ?? f.per_domain_delay_ms,
      concurrency: cfg.concurrency ?? f.concurrency,
    }));
  };

  const hasTarget = !!(form.keywords.trim() || form.seed_urls.trim() || form.sitemap_urls.trim());

  const handleCreate = async (startNow) => {
    if (!hasTarget) {
      toast.error("Enter at least one keyword, seed URL or sitemap");
      return;
    }
    const created = await api.createCrawl(buildConfig());
    setActiveId(created.id);
    await refresh();
    toast.success(`Crawl "${created.name}" created`);
    if (startNow) {
      await api.startCrawl(created.id);
      toast.info("Crawl started ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â collecting data");
      setTimeout(() => { poll(); refresh(); }, 800);
    }
  };

  const handleStart = async () => {
    try {
      await api.startCrawl(crawl.id);
      toast.info(crawl.pages_crawled > 0 ? "Restarted from scratch" : "Crawl started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start crawl");
    }
    setTimeout(() => { poll(); refresh(); }, 800);
  };
  const handleResume = async () => {
    try {
      await api.resumeCrawl(crawl.id);
      toast.success("Resumed ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â continuing from saved frontier");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume");
    }
    setTimeout(() => { poll(); refresh(); }, 800);
  };
  const handlePause = async () => {
    try {
      await api.pauseCrawl(crawl.id);
      toast.warning("Pausing ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â frontier will be saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause");
    }
  };
  const handleStop = async () => {
    await api.stopCrawl(crawl.id);
    toast.warning("Stopping crawlÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¦");
  };
  const handleExtend = async () => {
    try {
      await api.extendCrawl(crawl.id, "extend");
      toast.success("Extended ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â new page + time budget granted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend");
    }
    setTimeout(() => { poll(); refresh(); }, 800);
  };

  const speed = detail?.live?.speed ?? 0;
  const runningCount = crawls.filter((c) => c.status === "running").length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1600px]">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0 max-w-full">
          <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Control Room</h2>
          <p className="text-sm text-slate-500 mt-1">Configure a crawl, launch it, and watch data stream in live.</p>
          {crawl && (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="active-crawl-header">
              <span className="font-display font-semibold text-slate-200 truncate max-w-full">{crawl.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">ID</span>
              <CrawlId id={crawl.id} className="text-slate-300" testid="active-crawl-id" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {runningCount > 0 && (
            <div data-testid="running-jobs-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 font-mono text-xs uppercase tracking-wider text-cyan-300">
              <CircleDot className="h-3 w-3 pulse-dot" /> {runningCount} running
            </div>
          )}
          {crawl && (
            <div data-testid="active-crawl-status" className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-md border font-mono text-xs uppercase tracking-wider",
              isRunning ? "text-cyan-300 border-cyan-500/50 bg-cyan-500/10"
                : crawl.status === "completed" ? "text-emerald-300 border-emerald-500/50 bg-emerald-500/10"
                : crawl.status === "paused" ? "text-amber-300 border-amber-500/50 bg-amber-500/10"
                : "text-slate-300 border-slate-600 bg-slate-800/40")}>
              {isRunning && <CircleDot className="h-3 w-3 pulse-dot" />}
              {crawl.status}
            </div>
          )}
        </div>
      </header>

      <div className="grid grid-cols-12 gap-6">
        {/* Setup form */}
        <div className="col-span-12 lg:col-span-5 space-y-5 rounded-lg border border-border bg-[#0B0F18] p-5">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-cyan-400" />
            <h3 className="font-display font-bold text-base">New Crawl</h3>
          </div>

          <PromptToCrawl
            onLaunched={(c) => { setActiveId(c.id); refresh(); setTimeout(() => { poll(); refresh(); }, 900); }} />

          <UploadFiles crawls={crawls}
            onUploaded={(c) => { setActiveId(c.id); refresh(); }} />

          <EmpireSource onStarted={refresh} />

          <CrawlPresets buildConfig={buildConfig} applyConfig={applyConfig} canSave={hasTarget}
            onLaunched={(c) => { setActiveId(c.id); refresh(); setTimeout(() => { poll(); refresh(); }, 900); }} />

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Job Name</Label>
            <Input data-testid="crawl-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Rust tutorials corpus" className="bg-[#0E1420] border-border font-mono text-sm" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Keywords <span className="text-slate-600">(searches the web ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· comma / newline separated)</span></Label>
            <Textarea data-testid="crawl-keywords-input" value={form.keywords} onChange={(e) => set("keywords", e.target.value)}
              placeholder="machine learning tutorial, rust ownership, ..." rows={2}
              className="bg-[#0E1420] border-border font-mono text-sm resize-none" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Seed URLs <span className="text-slate-600">(starting points ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· comma / newline separated)</span></Label>
            <Textarea data-testid="crawl-seed-urls-input" value={form.seed_urls} onChange={(e) => set("seed_urls", e.target.value)}
              placeholder="https://example.com/docs" rows={2}
              className="bg-[#0E1420] border-border font-mono text-sm resize-none" />
          </div>

          <div className="rounded-md border border-border bg-[#0E1420]/60 p-3.5 space-y-3">
            <div className="flex items-center gap-2">
              <Map className="h-3.5 w-3.5 text-sky-400" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Sitemap seeding</span>
              <span className="ml-auto font-mono text-[10px] text-slate-600">grabs every article, no link guessing</span>
            </div>
            <Textarea data-testid="crawl-sitemap-input" value={form.sitemap_urls}
              onChange={(e) => set("sitemap_urls", e.target.value)} rows={2}
              placeholder="example.com  ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â·  or  https://example.com/sitemap.xml"
              className="bg-[#0B0F18] border-border font-mono text-sm resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">URL filter</Label>
                <Input data-testid="crawl-sitemap-filter-input" value={form.sitemap_filter}
                  onChange={(e) => set("sitemap_filter", e.target.value)} placeholder="/blog/"
                  className="bg-[#0B0F18] border-border font-mono text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Max sitemap URLs</Label>
                <Input data-testid="crawl-sitemap-limit-input" type="number" min={1} max={5000}
                  value={form.sitemap_limit} onChange={(e) => set("sitemap_limit", e.target.value)}
                  className="bg-[#0B0F18] border-border font-mono text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" data-testid="sitemap-preview-button" onClick={previewSitemap}
                disabled={smBusy} className="h-8 border-border hover:bg-slate-800">
                {smBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                <span className="ml-1.5 font-mono text-[11px]">Preview sitemap</span>
              </Button>
              {sitemapInfo && (
                <span data-testid="sitemap-preview-result" className="font-mono text-[11px] text-sky-300">
                  {fmtNum(sitemapInfo.count)} URLs ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· {sitemapInfo.sitemaps.length} sitemap(s)
                </span>
              )}
            </div>
            {sitemapInfo?.sample?.length > 0 && (
              <pre className="terminal-scroll max-h-[80px] overflow-auto rounded border border-border bg-[#04060A] p-2 font-mono text-[10px] text-slate-500">
                {sitemapInfo.sample.slice(0, 6).join("\n")}
              </pre>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Extraction Mode</Label>
              <Select value={form.extraction_mode} onValueChange={(v) => set("extraction_mode", v)}>
                <SelectTrigger data-testid="crawl-extraction-mode-select" className="bg-[#0E1420] border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXTRACTION_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Relevance Match</Label>
              <Select value={form.relevance_match} onValueChange={(v) => set("relevance_match", v)}>
                <SelectTrigger data-testid="crawl-relevance-match-select" className="bg-[#0E1420] border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Contains ANY keyword</SelectItem>
                  <SelectItem value="all">Contains ALL keywords</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Relevance Filter Keywords <span className="text-slate-600">(only keep pages containing these)</span></Label>
            <Input data-testid="crawl-relevance-filter-input" value={form.relevance_keywords}
              onChange={(e) => set("relevance_keywords", e.target.value)}
              placeholder="leave empty to keep everything" className="bg-[#0E1420] border-border font-mono text-sm" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Time Limit</Label>
              <span className="font-mono text-xs text-cyan-400">{form.time_limit_seconds >= 3600 ? `${Math.floor(form.time_limit_seconds / 3600)}h ${Math.floor((form.time_limit_seconds % 3600) / 60)}m` : `${Math.floor(form.time_limit_seconds / 60)}m ${form.time_limit_seconds % 60}s`}</span>
            </div>
            <Slider data-testid="crawl-time-limit-slider" min={30} max={86400} step={60}
              value={[form.time_limit_seconds]} onValueChange={(v) => set("time_limit_seconds", v[0])} />
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Max Pages</Label>
              <Input data-testid="crawl-max-pages-input" type="number" min={1} value={form.max_pages}
                onChange={(e) => set("max_pages", e.target.value)} className="bg-[#0E1420] border-border font-mono text-sm" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border bg-[#0E1420] px-3 py-2.5">
              <Label className="text-xs text-slate-400">Same domain only</Label>
              <Switch data-testid="crawl-same-domain-switch" checked={form.same_domain_only}
                onCheckedChange={(v) => set("same_domain_only", v)} />
            </div>
          </div>
          {/* AI Quality Filter */}
          <div className="rounded-md border border-border bg-[#0E1420]/60 p-3.5 space-y-3">
            <div className="flex items-center gap-2">
              <Award className="h-3.5 w-3.5 text-cyan-400" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">AI Quality Filter</span>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Score + filter pages with Gemini</Label>
              <Switch data-testid="crawl-llm-quality-switch" checked={form.llm_quality_check}
                onCheckedChange={(v) => set("llm_quality_check", v)} />
            </div>
            {form.llm_quality_check && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Minimum quality score</Label>
                <Input data-testid="crawl-llm-quality-threshold-input" type="number" min={0} max={1} step={0.05}
                  value={form.llm_quality_threshold}
                  onChange={(e) => set("llm_quality_threshold", e.target.value)}
                  className="bg-[#0E1420] border-border font-mono text-sm" />
              </div>
            )}
          </div>

          {/* Politeness */}
          <div className="rounded-md border border-border bg-[#0E1420]/60 p-3.5 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Politeness</span>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Respect robots.txt</Label>
              <Switch data-testid="crawl-respect-robots-switch" checked={form.respect_robots}
                onCheckedChange={(v) => set("respect_robots", v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Per-domain delay (ms)</Label>
                <Input data-testid="crawl-domain-delay-input" type="number" min={0} step={100}
                  value={form.per_domain_delay_ms} onChange={(e) => set("per_domain_delay_ms", e.target.value)}
                  className="bg-[#0B0F18] border-border font-mono text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Concurrency</Label>
                <Input data-testid="crawl-concurrency-input" type="number" min={1} max={16}
                  value={form.concurrency} onChange={(e) => set("concurrency", e.target.value)}
                  className="bg-[#0B0F18] border-border font-mono text-sm" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <Button data-testid="crawl-create-start-button" onClick={() => handleCreate(true)}
              className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
              <Play className="h-4 w-4 mr-1.5" /> Create & Start
            </Button>
            <Button data-testid="crawl-create-button" variant="outline" onClick={() => handleCreate(false)}
              className="border-border hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1.5" /> Create
            </Button>
          </div>
        </div>

        {crawl && (
          <div className="col-span-12 lg:col-span-5 lg:order-last">
            <SchedulePanel key={crawl.id} crawlId={crawl.id} />
          </div>
        )}

        {/* Live monitor */}
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric icon={FileText} label="Pages" value={fmtNum(crawl?.pages_crawled || 0)} accent="text-cyan-400" />
            <Metric icon={Layers} label="Queue" value={fmtNum(crawl?.queue_depth || 0)} accent="text-amber-400" />
            <Metric icon={Gauge} label="Pages/sec" value={Number(speed).toFixed(2)} accent="text-emerald-400" />
            <Metric icon={Clock} label="Elapsed" value={`${Math.floor(elapsed)}s`} accent="text-purple-400" />
            <Metric icon={HardDrive} label="Scraped" value={fmtBytes(crawl?.bytes_scraped || 0)} accent="text-sky-400" />
            <Metric icon={Zap} label="Limit" value={`${crawl?.time_limit_seconds || form.time_limit_seconds}s`} accent="text-orange-400" />
            <Metric icon={Ban} label="Robots blocked" value={fmtNum(crawl?.blocked_by_robots || 0)} accent="text-rose-400" />
            <Metric icon={Activity} label="Avg p/s" value={Number(detail?.live?.avg_speed || 0).toFixed(2)} accent="text-teal-400" />
          </div>

          <ThroughputChart samples={samples || []} statusCounts={statusCounts || {}} />

          <div className="flex gap-3 flex-wrap items-center">
            {isRunning ? (
              <>
                <Button data-testid="crawl-pause-button" onClick={handlePause}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                  <Pause className="h-4 w-4 mr-1.5" /> Pause
                </Button>
                <Button data-testid="crawl-stop-button" onClick={handleStop}
                  className="bg-rose-500 hover:bg-rose-400 text-white font-semibold">
                  <Square className="h-4 w-4 mr-1.5" /> Emergency Stop
                </Button>
              </>
            ) : (
              <>
                {canResume && (
                  <Button data-testid="crawl-resume-button" onClick={handleResume}
                    className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                    <Play className="h-4 w-4 mr-1.5" /> Resume ({fmtNum(crawl.queue_depth)} queued)
                  </Button>
                )}
                <Button data-testid="crawl-start-button" onClick={handleStart} disabled={!crawl}
                  variant={canResume ? "outline" : "default"}
                  className={cn("font-semibold disabled:opacity-40",
                    canResume ? "border-border hover:bg-slate-800" : "bg-cyan-500 hover:bg-cyan-400 text-slate-950")}>
                  {canResume ? <RotateCcw className="h-4 w-4 mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                  {!crawl ? "No job selected" : canResume ? "Restart (clears pages)" : "Start Crawl"}
                </Button>
                {crawl && crawl.pages_crawled > 0 && (
                  <Button data-testid="crawl-extend-button" onClick={handleExtend} variant="outline"
                    className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 font-semibold">
                    <TrendingUp className="h-4 w-4 mr-1.5" /> Extend Crawl
                  </Button>
                )}
              </>
            )}
            <label className="ml-auto flex items-center gap-2 text-xs text-slate-400 font-mono">
              <Switch checked={autoScroll} onCheckedChange={setAutoScroll} data-testid="log-autoscroll-switch" />
              auto-scroll
            </label>
          </div>

          {/* Terminal */}
          <div className="rounded-lg border border-border bg-[#04060A] overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-[#0B0F18] flex-wrap">
              <Activity className="h-3.5 w-3.5 text-cyan-400" />
              <span className="font-mono text-xs uppercase tracking-wider text-slate-400">
                {viewingLive ? "Live Logs" : "Archived Run"}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Select value={runId} onValueChange={setRunId}>
                  <SelectTrigger data-testid="run-history-select"
                    className="h-7 w-[230px] bg-[#0E1420] border-border text-[11px] font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live">Live view</SelectItem>
                    {runs.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        Run #{r.run_number} ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· {r.status} ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· {r.pages_end - r.pages_start} pgs ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â· {r.started_at?.slice(5, 16).replace("T", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {runs.length > 0 && (
                  <Button size="sm" variant="ghost" data-testid="run-history-clear-button" onClick={clearHistory}
                    className="h-7 text-slate-500 hover:text-rose-400">
                    <History className="h-3.5 w-3.5 mr-1" /><span className="font-mono text-[10px]">clear</span>
                  </Button>
                )}
              </div>
            </div>
            <div ref={logRef} data-testid="live-log-terminal"
              className="terminal-scroll h-[340px] overflow-y-auto p-4 font-mono text-xs leading-relaxed space-y-0.5">
              {(!logs || logs.length === 0) && (
                <p className="text-slate-600">// {viewingLive ? "waiting for crawl activityÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¦" : "no logs stored for this run"}</p>
              )}
              {logs?.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-slate-700 shrink-0">{l.ts.slice(11, 19)}</span>
                  <span className={cn("shrink-0 w-16", LOG_COLORS[l.level] || "text-slate-400")}>[{l.level}]</span>
                  <span className="text-slate-300 break-all">{l.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
